import { STORAGE_KEYS } from './constants.js';

/**
 * Centralized application state
 * This provides a single source of truth for all application state
 */
const state = {
  // Scanning state
  shouldScan: true,
  scanTimeoutId: null,

  // Barcode reader instance
  barcodeReader: null,

  // Current session data
  currentClientId: null,
  currentDossierId: null,

  // UI state
  isEditing: false
};

/**
 * State change listeners
 */
const listeners = new Map();

/**
 * Gets the current state value
 * @param {string} key - State key
 * @returns {*} State value
 */
export function getState(key) {
  return state[key];
}

/**
 * Updates a state value and notifies listeners
 * @param {string} key - State key
 * @param {*} value - New value
 */
export function setState(key, value) {
  const oldValue = state[key];
  state[key] = value;

  // Notify listeners
  const keyListeners = listeners.get(key);
  if (keyListeners) {
    keyListeners.forEach(callback => callback(value, oldValue));
  }
}

/**
 * Subscribes to state changes
 * @param {string} key - State key to watch
 * @param {Function} callback - Callback function (newValue, oldValue)
 * @returns {Function} Unsubscribe function
 */
export function subscribe(key, callback) {
  if (!listeners.has(key)) {
    listeners.set(key, new Set());
  }
  listeners.get(key).add(callback);

  return () => {
    listeners.get(key).delete(callback);
  };
}

/**
 * Gets client ID from state or localStorage
 * @returns {string|null} Client ID
 */
export function getClientId() {
  if (state.currentClientId) {
    return state.currentClientId;
  }
  const stored = localStorage.getItem(STORAGE_KEYS.CLIENT_ID);
  if (stored) {
    state.currentClientId = stored;
  }
  return stored;
}

/**
 * Sets client ID in state and localStorage
 * @param {string|null} clientId - Client ID
 */
export function setClientId(clientId) {
  state.currentClientId = clientId;
  if (clientId) {
    localStorage.setItem(STORAGE_KEYS.CLIENT_ID, clientId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.CLIENT_ID);
  }
}

/**
 * Gets dossier ID from state or localStorage
 * @returns {string|null} Dossier ID
 */
export function getDossierId() {
  if (state.currentDossierId) {
    return state.currentDossierId;
  }
  const stored = localStorage.getItem(STORAGE_KEYS.DOSSIER_ID);
  if (stored) {
    state.currentDossierId = stored;
  }
  return stored;
}

/**
 * Sets dossier ID in state and localStorage
 * @param {string|null} dossierId - Dossier ID
 */
export function setDossierId(dossierId) {
  state.currentDossierId = dossierId;
  if (dossierId) {
    localStorage.setItem(STORAGE_KEYS.DOSSIER_ID, dossierId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.DOSSIER_ID);
  }
}

/**
 * Clears all session data
 */
export function clearSession() {
  state.currentClientId = null;
  state.currentDossierId = null;
  localStorage.removeItem(STORAGE_KEYS.CLIENT_ID);
  localStorage.removeItem(STORAGE_KEYS.DOSSIER_ID);
}

/**
 * Resets scanning state
 */
export function resetScanState() {
  if (state.scanTimeoutId) {
    clearTimeout(state.scanTimeoutId);
    state.scanTimeoutId = null;
  }
  state.shouldScan = true;
}

/**
 * Stops scanning
 */
export function stopScanning() {
  state.shouldScan = false;
  if (state.scanTimeoutId) {
    clearTimeout(state.scanTimeoutId);
    state.scanTimeoutId = null;
  }
}

/**
 * Starts scanning
 */
export function startScanning() {
  state.shouldScan = true;
}

/**
 * Sets the barcode reader instance
 * @param {object} reader - BarcodeReader instance
 */
export function setBarcodeReader(reader) {
  state.barcodeReader = reader;
}

/**
 * Gets the barcode reader instance
 * @returns {object|null} BarcodeReader instance
 */
export function getBarcodeReader() {
  return state.barcodeReader;
}

/**
 * Sets scan timeout ID
 * @param {number|null} timeoutId - Timeout ID
 */
export function setScanTimeoutId(timeoutId) {
  state.scanTimeoutId = timeoutId;
}

/**
 * Gets scan timeout ID
 * @returns {number|null} Timeout ID
 */
export function getScanTimeoutId() {
  return state.scanTimeoutId;
}

/**
 * Checks if scanning is enabled
 * @returns {boolean} True if scanning should continue
 */
export function shouldContinueScanning() {
  return state.shouldScan;
}

// Export state for debugging (read-only access preferred)
export { state };
